import { Ref, computed, ref, watch } from 'vue'
import {
  BuildingProperties,
  ComplexProperties,
  LevelProperties,
  QUICK_SELECTS,
  QuickSelect,
  QuickSelectType,
  RealEstateProperties,
  RoomCapacity,
  RoomProperties,
  RoomTypeFilter
} from '@/components/types'
import { Store, defineStore, storeToRefs } from 'pinia'
import isEqual from 'lodash/isEqual'
import cloneDeep from 'lodash/cloneDeep'
import useFeatures, { useFeature } from '@/pinia/features'
import { CoreType } from '@/api-client/models/shared/Contracts'
import md5 from 'md5'
import { BucketSize } from '@/api-client/models/indoorclimate/Contracts'
import { quickSelectToTimeBefore } from '@/utils/calendar-utils'
import { merge } from 'lodash'
import { calculateBucketSizes } from '@/utils/calculateBucketSizes'
import { ProjectStatus, ProjectType } from '@/api-client/models/project/Contracts'
import { zoomToEntities } from './useMapbox'
import { LevelOfDetail, useLevelOfDetail } from '@/pinia/levelOfDetail'
import { TicketStatus } from '@/api-client/models/tickets/Contracts'

export enum FeatureType {
  Utilization = 'utilization',
  IndoorClimate = 'indoor_climate',
  BuildingScore = 'building_score',
  ProjectAnalysisInternal = 'project_analysis_internal',
  ProjectAnalysisExternal = 'project_analysis_external',
  PortfolioAnalysis = 'portfolio_analysis',
  EnergyAnalysis = 'energy_analysis',
  TicketAnalysis = 'ticket_analysis',
  AggregateAnalysis = 'aggregate_analysis'
}

enum FilterType {
  Calendar = 'calendar',
  Year = 'year',
  Complex = 'complex',
  RealEstate = 'realEstate',
  Buildings = 'buildings',
  Level = 'level',
  RoomTypes = 'roomTypes',
  RoomCapacity = 'roomCapacity',
  OnlyBookable = 'onlyBookable',
  BucketSize = 'bucketSize',
  LevelOfDetail = 'levelOfDetail',
  Budget = 'budget',
  Room = 'room',
  ProjectTypes = 'projectTypes',
  ProjectStatuses = 'projectStatuses',
  TicketTypes = 'ticketTypes',
  TicketStatuses = 'ticketStatuses',
  InformationResponse = 'informationResponse',
  OnlyBusinessDays = 'onlyBusinessDays',
  OnlyBusinessHours = 'onlyBusinessHours',
  Measurement = 'measurement',
  MeasurementFields = 'measurementFields',
  MeasurementTags = 'measurementTags'
}

type Filter<Type extends FilterType = FilterType> = Type extends 'calendar'
  ? { type: 'calendar'; value?: CalendarFilters }
  : Type extends 'year'
  ? { type: 'year'; value: number }
  : Type extends 'complex'
  ? { type: 'complex'; value?: ComplexProperties }
  : Type extends 'realEstate'
  ? { type: 'realEstate'; value?: RealEstateProperties }
  : Type extends 'buildings'
  ? { type: 'buildings'; value: BuildingProperties[] }
  : Type extends 'level'
  ? { type: 'level'; value?: LevelProperties }
  : Type extends 'roomTypes'
  ? { type: 'roomTypes'; value: RoomTypeFilter[] }
  : Type extends 'roomCapacity'
  ? { type: 'roomCapacity'; value: RoomCapacity[] }
  : Type extends 'onlyBookable'
  ? { type: 'onlyBookable'; value: boolean }
  : Type extends 'bucketSize'
  ? { type: 'bucketSize'; value?: BucketSize }
  : Type extends 'levelOfDetail'
  ? { type: 'levelOfDetail'; value: LevelOfDetail }
  : Type extends 'room'
  ? { type: 'room'; value?: RoomProperties }
  : Type extends 'budget'
  ? { type: 'budget'; value?: { from?: number; to?: number } }
  : Type extends 'projectTypes'
  ? { type: 'projectTypes'; value: ProjectType[] }
  : Type extends 'projectStatuses'
  ? { type: 'projectStatuses'; value: ProjectStatus[] }
  : Type extends 'ticketTypes'
  ? { type: 'ticketTypes'; value: string[] }
  : Type extends 'ticketStatuses'
  ? { type: 'ticketStatuses'; value: TicketStatus[] }
  : Type extends 'informationResponse'
  ? { type: 'informationResponse'; value: { buildings?: number; rooms?: number } }
  : Type extends 'onlyBusinessDays'
  ? { type: 'onlyBusinessDays'; value: boolean }
  : Type extends 'onlyBusinessHours'
  ? { type: 'onlyBusinessHours'; value: boolean }
  : Type extends 'measurement'
  ? { type: 'measurement'; value?: string }
  : Type extends 'measurementFields'
  ? { type: 'measurementFields'; value?: string[] }
  : Type extends 'measurementTags'
  ? { type: 'measurementTags'; value?: { [key: string]: string[] } }
  : never

export type CalendarFilters = {
  from?: string
  to?: string
  quickSelect?: QuickSelectType
}

const emptyFilter: Partial<{ [K in FilterType]: () => Filter<K> }> = {
  calendar: () => ({
    type: 'calendar',
    value: {}
  }),
  year: () => ({ type: 'year', value: new Date().getFullYear() }),
  complex: () => ({ type: 'complex', value: undefined }),
  realEstate: () => ({ type: 'realEstate', value: undefined }),
  buildings: () => ({ type: 'buildings', value: [] }),
  level: () => ({ type: 'level', value: undefined }),
  room: () => ({ type: 'room', value: undefined }),
  roomTypes: () => ({ type: 'roomTypes', value: [] }),
  roomCapacity: () => ({ type: 'roomCapacity', value: [] }),
  budget: () => ({ type: 'budget', value: undefined }),
  onlyBookable: () => ({ type: 'onlyBookable', value: false }),
  bucketSize: () => ({ type: 'bucketSize', value: undefined }),
  projectTypes: () => ({ type: 'projectTypes', value: [] }),
  projectStatuses: () => ({ type: 'projectStatuses', value: [] }),
  ticketTypes: () => ({ type: 'ticketTypes', value: [] }),
  ticketStatuses: () => ({ type: 'ticketStatuses', value: [] }),
  informationResponse: () => ({ type: 'informationResponse', value: {} }),
  levelOfDetail: () => ({ type: 'levelOfDetail', value: 'LEVEL' }),
  onlyBusinessDays: () => ({ type: 'onlyBusinessDays', value: false }),
  onlyBusinessHours: () => ({ type: 'onlyBusinessHours', value: false })
}

type Filters<F extends FilterType> = {
  [K in F]: Filter<K>['value']
}

const geoFilters = [
  FilterType.Complex,
  FilterType.RealEstate,
  FilterType.Buildings,
  FilterType.Level,
  FilterType.Room
] as const
type GeoFilterTypes = (typeof geoFilters)[number]
export type GeoFilters = Filters<GeoFilterTypes>

export const utilizationFilters = [
  FilterType.Calendar,
  ...geoFilters,
  FilterType.RoomTypes,
  FilterType.RoomCapacity,
  FilterType.OnlyBookable,
  FilterType.OnlyBusinessDays,
  FilterType.OnlyBusinessHours,
  FilterType.InformationResponse
] as const
type UtilizationFilterTypes = (typeof utilizationFilters)[number]

export const indoorClimateFilters = [
  FilterType.Calendar,
  ...geoFilters,
  FilterType.RoomTypes,
  FilterType.BucketSize,
  FilterType.OnlyBusinessDays,
  FilterType.OnlyBusinessHours,
  FilterType.LevelOfDetail,
  FilterType.InformationResponse
] as const
type IndoorClimateFilterTypes = (typeof indoorClimateFilters)[number]

export const buildingScoreFilters = [
  FilterType.Year,
  FilterType.Complex,
  FilterType.Buildings,
  FilterType.InformationResponse
] as const
type BuildingScoreFilterTypes = (typeof buildingScoreFilters)[number]

export const projectFilters = [
  FilterType.Complex,
  FilterType.Buildings,
  FilterType.ProjectTypes,
  FilterType.ProjectStatuses,
  FilterType.InformationResponse,
  FilterType.Budget
] as const
type ProjectFilterTypes = (typeof projectFilters)[number]

export const ticketFilters = [
  FilterType.Calendar,
  ...geoFilters,
  FilterType.TicketTypes,
  FilterType.TicketStatuses,
  FilterType.InformationResponse
] as const
type TicketFilterTypes = (typeof ticketFilters)[number]

export const portfolioFilters = [
  FilterType.Complex,
  FilterType.RealEstate,
  FilterType.Buildings,
  FilterType.InformationResponse
] as const
type PortfolioFilterTypes = (typeof portfolioFilters)[number]

export const energyFilters = [
  FilterType.Complex,
  FilterType.RealEstate,
  FilterType.Buildings,
  FilterType.InformationResponse
] as const
type EnergyFilterTypes = (typeof energyFilters)[number]

export const aggregateFilters = [
  FilterType.Calendar,
  ...geoFilters,
  FilterType.RealEstate,
  FilterType.InformationResponse,
  FilterType.BucketSize,
  FilterType.OnlyBusinessHours,
  FilterType.OnlyBusinessDays,
  FilterType.Measurement,
  FilterType.MeasurementFields,
  FilterType.MeasurementTags
] as const
type AggregateFilterTypes = (typeof aggregateFilters)[number]

export type FeatureFilters<T extends FeatureType = FeatureType> = T extends FeatureType.Utilization
  ? Filters<UtilizationFilterTypes> & { type: FeatureType.Utilization }
  : T extends FeatureType.IndoorClimate
  ? Filters<IndoorClimateFilterTypes> & { type: FeatureType.IndoorClimate }
  : T extends FeatureType.BuildingScore
  ? Filters<BuildingScoreFilterTypes> & { type: FeatureType.BuildingScore }
  : T extends FeatureType.ProjectAnalysisInternal
  ? Filters<ProjectFilterTypes> & { type: FeatureType.ProjectAnalysisInternal }
  : T extends FeatureType.ProjectAnalysisExternal
  ? Filters<ProjectFilterTypes> & { type: FeatureType.ProjectAnalysisExternal }
  : T extends FeatureType.TicketAnalysis
  ? Filters<TicketFilterTypes> & { type: FeatureType.TicketAnalysis }
  : T extends FeatureType.PortfolioAnalysis
  ? Filters<PortfolioFilterTypes> & { type: FeatureType.PortfolioAnalysis }
  : T extends FeatureType.EnergyAnalysis
  ? Filters<EnergyFilterTypes> & { type: FeatureType.EnergyAnalysis }
  : T extends FeatureType.AggregateAnalysis
  ? Filters<AggregateFilterTypes> & { type: FeatureType.AggregateAnalysis }
  : never

export const featureFilters: { [K in FeatureType]: readonly FilterType[] } = {
  utilization: utilizationFilters,
  indoor_climate: indoorClimateFilters,
  building_score: buildingScoreFilters,
  project_analysis_internal: projectFilters,
  project_analysis_external: projectFilters,
  ticket_analysis: ticketFilters,
  portfolio_analysis: portfolioFilters,
  energy_analysis: energyFilters,
  aggregate_analysis: aggregateFilters
}

export type FilterMetadata = {
  hasGeographicalFilter: boolean
  entityType: CoreType | undefined
}

const emptyState = <T extends FeatureType>(type: T, filters: FilterType[]): FeatureFilters<T> => {
  const state = {
    type
  } as FeatureFilters<T>
  filters.forEach((f) => {
    const reset = emptyFilter[f]
    if (reset) {
      state[f] = reset().value
    }
  })
  return state
}

const pickFilters = <T extends FeatureType, P extends FilterType>(
  filters: FeatureFilters<T>,
  filterTypes: readonly P[]
): GeoFilters => {
  return Object.fromEntries(
    Object.entries(filters).filter(([key]) => filterTypes.includes(key as P))
  ) as GeoFilters
}

const analyticTypeToFeaturePath: Partial<{ [K in FeatureType]: string }> = {
  utilization: 'home/analytics/analyticareas/usage',
  indoor_climate: 'home/analytics/analyticareas/indoorclimate',
  building_score: 'home/analytics/portfolio/buildingscore',
  project_analysis_internal: 'home/assets/projects/internal',
  project_analysis_external: 'home/assets/projects/external',
  ticket_analysis: 'home/assets/todo/ticket',
  portfolio_analysis: 'home/analytics/analyticareas/portfolio',
  energy_analysis: 'home/analytics/analyticareas/energy/data/*'
}

const defaultFeaturePath = 'home/analytics/*/*/data/*'

function createStore<T extends FeatureType>(type: T) {
  return defineStore(type, () => {
    const { feature } = useFeature(analyticTypeToFeaturePath[type] ?? defaultFeaturePath)

    const { currentLevelOfDetail } = storeToRefs(useLevelOfDetail())

    const defaultFilters = ref<Partial<FeatureFilters<T>> | undefined>()

    const filters = ref<FilterType[]>(
      (feature.value?.props?.filters ?? featureFilters[type]) as FilterType[]
    )

    watch(
      feature,
      () => {
        const filtersFromFeature = feature.value?.props?.filters ?? featureFilters[type]
        if (filtersFromFeature && !isEqual(filters.value, filtersFromFeature)) {
          const sameFilters = filtersFromFeature.filter((f) => filters.value.includes(f))
          const changedFilters = filters.value
            .filter((f) => !sameFilters.includes(f))
            .concat(filtersFromFeature.filter((f) => !sameFilters.includes(f)))
          const removedFilters = filters.value.filter((f) => !filtersFromFeature.includes(f))
          filters.value = filtersFromFeature
          reset(changedFilters)
          for (const f of removedFilters) {
            current.value[f] = undefined
          }
          submit()
        }
      },
      { immediate: true }
    )

    // const filters = featureFilters[type]

    const getNewState = (type: T, filters: FilterType[]): FeatureFilters<T> =>
      merge(emptyState(type, filters), defaultFilters.value)

    const current: Ref<FeatureFilters<T>> = ref<FeatureFilters<T>>(
      getNewState(type, filters.value)
    ) as Ref<FeatureFilters<T>>
    const submitted: Ref<FeatureFilters<T>> = ref<FeatureFilters<T>>(
      getNewState(type, filters.value)
    ) as Ref<FeatureFilters<T>>

    const currentGeographicalFilters = computed(() => pickFilters(current.value, geoFilters))

    const submittedGeographicalFilters = computed(() => pickFilters(submitted.value, geoFilters))

    function reset(resetFilters?: FilterType[]) {
      current.value = {
        ...current.value,
        ...getNewState(type, resetFilters ?? filters.value)
      }
    }

    function $reset() {
      reset()
    }

    function submit() {
      const h1 = md5(JSON.stringify(current.value))
      const h2 = md5(JSON.stringify(submitted.value))
      if (h1 != h2) {
        submitted.value = JSON.parse(JSON.stringify(current.value))
      }
    }
    function updateInformationResponse(informationResponse: {
      buildings?: number
      rooms?: number
    }) {
      current.value.informationResponse = informationResponse
    }

    function geoFilter(filters: FeatureFilters): CoreType | undefined {
      if (filters['room'] !== undefined) return 'SPACE'
      if (filters['level'] !== undefined) return 'LEVEL'
      if (filters['buildings']?.length > 0) return 'CONSTRUCTION'
      if ('realEstate' in filters && filters['realEstate'] !== undefined) return 'REAL_ESTATE'
      if (filters['complex'] !== undefined) return 'COMPLEX'
      return undefined
    }

    function importGeographicalFilters(state: GeoFilters) {
      current.value = {
        ...current.value,
        ...state
      }
      submit()
    }

    function resetBucketSize() {
      if (
        'calendar' in current.value &&
        !current.value.calendar?.from &&
        !current.value.calendar?.to &&
        current.value.calendar?.quickSelect
      ) {
        const qs = current.value.calendar.quickSelect
        const quickSelect = QUICK_SELECTS.find((x) => x.name === qs) as QuickSelect
        const { from, to } = quickSelectToTimeBefore(new Date(), quickSelect)
        current.value.calendar.from = from
        current.value.calendar.to = to
      }

      if (
        'bucketSize' in current.value &&
        current.value.calendar?.from &&
        current.value.calendar?.to
      ) {
        const bucketSizes = calculateBucketSizes(
          current.value.calendar?.from,
          current.value.calendar?.to
        )
        const bucketSize =
          defaultFilters.value &&
          'bucketSize' in defaultFilters.value &&
          defaultFilters.value.bucketSize
            ? (defaultFilters.value.bucketSize as BucketSize)
            : undefined
        current.value.bucketSize =
          bucketSize && bucketSizes.indexOf(bucketSize) > -1 ? bucketSize : bucketSizes[0]
      }
    }

    const currentMeta = computed<FilterMetadata>(() => ({
      hasGeographicalFilter: geoFilter(current.value) !== undefined,
      entityType: geoFilter(current.value)
    }))

    const submittedMeta = computed<FilterMetadata>(() => ({
      hasGeographicalFilter: geoFilter(submitted.value) !== undefined,
      entityType: geoFilter(submitted.value)
    }))

    const isCurrentFilterSubmitted = computed(() => isEqual(current.value, submitted.value))

    watch(
      feature,
      () => {
        defaultFilters.value = cloneDeep(feature.value?.props?.defaultFilters)
        current.value = {
          ...current.value,
          ...defaultFilters.value
        }
        submit()
      },
      { immediate: true }
    )

    watch(
      submitted,
      (state) => {
        if ('calendar' in state) {
          if (state.calendar?.quickSelect) {
            const quickSelect = QUICK_SELECTS.find(
              (x) => x.name === state.calendar?.quickSelect
            ) as QuickSelect
            const { from, to } = quickSelectToTimeBefore(new Date(), quickSelect)
            state.calendar.from = from
            state.calendar.to = to
          }
        }
        resetBucketSize()
      },
      { immediate: true }
    )

    watch(submittedGeographicalFilters, (value) => {
      let entityId: string | undefined = undefined
      const zoomTreshold = 6
      const pitchTreshold = 60

      if (value.complex) {
        entityId = value.complex.belongsToId
      }

      if (value.realEstate) {
        entityId = value.realEstate.belongsToId
      }

      if (value.buildings.length > 1) {
        entityId = value.buildings[0].realEstateId
      }

      if (value.buildings.length === 1) {
        entityId = value.buildings[0].id
      }

      if (value.level) {
        entityId = value.level.id
      }

      if (value.room) {
        entityId = value.room.id
      }

      if (entityId) {
        setTimeout(() => {
          zoomToEntities([entityId!], true, {
            zoomTreshold,
            pitchTreshold,
            zoomTrigger: 'intersect'
          })
        }, 10)
      }
    })

    watch(
      currentLevelOfDetail,
      () => {
        if ('levelOfDetail' in current.value) {
          current.value.levelOfDetail = currentLevelOfDetail.value
          submit()
        }
      },
      { immediate: true }
    )

    /**
     * This is a workaround to avoid reloads even when a filter is changed to the same value it already holds.
     * currentStable will only trigger a reload when a filter has been changed to a different value.
     *
     * Both current and currentStable are exposed, since we found no way to make the $patch function work with a hidden state.
     * A much nicer solution would be to use a hidden state and expose a function to update the state.
     */
    const currentStable = ref<FeatureFilters<T>>(emptyState(type, filters.value)) as Ref<
      FeatureFilters<T>
    >

    watch(
      current,
      (state) => {
        const h1 = md5(JSON.stringify(state))
        const h2 = md5(JSON.stringify(currentStable.value))
        if (h1 != h2) {
          currentStable.value = cloneDeep(state)
        }
      },
      { deep: true }
    )

    return {
      current,
      currentStable,
      currentMeta,
      submitted,
      submittedMeta,
      currentGeographicalFilters,
      submittedGeographicalFilters,
      reset,
      $reset,
      submit,
      importGeographicalFilters,
      resetBucketSize,
      isCurrentFilterSubmitted,
      updateInformationResponse
    }
  })
}

const stores: { [K in FeatureType]: ReturnType<typeof createStore<K>> } = {
  utilization: createStore(FeatureType.Utilization),
  indoor_climate: createStore(FeatureType.IndoorClimate),
  building_score: createStore(FeatureType.BuildingScore),
  project_analysis_internal: createStore(FeatureType.ProjectAnalysisInternal),
  project_analysis_external: createStore(FeatureType.ProjectAnalysisExternal),
  ticket_analysis: createStore(FeatureType.TicketAnalysis),
  portfolio_analysis: createStore(FeatureType.PortfolioAnalysis),
  energy_analysis: createStore(FeatureType.EnergyAnalysis),
  aggregate_analysis: createStore(FeatureType.AggregateAnalysis)
}

export type FilterStore<T extends FeatureType = FeatureType> = T extends FeatureType.Utilization
  ? ReturnType<(typeof stores)[FeatureType.Utilization]>
  : T extends FeatureType.IndoorClimate
  ? ReturnType<(typeof stores)[FeatureType.IndoorClimate]>
  : T extends FeatureType.BuildingScore
  ? ReturnType<(typeof stores)[FeatureType.BuildingScore]>
  : T extends FeatureType.ProjectAnalysisInternal
  ? ReturnType<(typeof stores)[FeatureType.ProjectAnalysisInternal]>
  : T extends FeatureType.ProjectAnalysisExternal
  ? ReturnType<(typeof stores)[FeatureType.ProjectAnalysisExternal]>
  : T extends FeatureType.TicketAnalysis
  ? ReturnType<(typeof stores)[FeatureType.TicketAnalysis]>
  : T extends FeatureType.PortfolioAnalysis
  ? ReturnType<(typeof stores)[FeatureType.PortfolioAnalysis]>
  : T extends FeatureType.EnergyAnalysis
  ? ReturnType<(typeof stores)[FeatureType.EnergyAnalysis]>
  : never

function useFilters<T extends FeatureType>(type: T) {
  return stores[type]()
}

const featureToType: { [K in string]: FeatureType } = {
  'home.analytics.analyticareas.usage': FeatureType.Utilization,
  'home.analytics.analyticareas.indoorclimate': FeatureType.IndoorClimate,
  'home.analytics.portfolio.buildingscore': FeatureType.BuildingScore,
  'home.assets.projects.internal': FeatureType.ProjectAnalysisInternal,
  'home.assets.projects.external': FeatureType.ProjectAnalysisExternal,
  'home.assets.todo.ticket': FeatureType.TicketAnalysis,
  'home.analytics.portfolio.portfolio': FeatureType.PortfolioAnalysis,
  'home.analytics.analyticareas.energy': FeatureType.EnergyAnalysis
}

function useCurrentFeatureFilters(
  features?: Store<'features', { featurePath: string[] }> // to allow to mock useFeatures for tests
) {
  const { featurePath } = storeToRefs(features ?? useFeatures())

  const currentFeature = computed<string | undefined>(() => featurePath.value.slice(0, 4).join('.'))
  return computed(() => {
    return currentFeature.value
      ? useFilters(featureToType[currentFeature.value] ?? FeatureType.AggregateAnalysis)
      : undefined
  })
}

export { useFilters, useCurrentFeatureFilters }
